import { IconChevronDown } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { UNSET } from '../lib/unset';
import type { Delivery } from '../routes/AccountWebhooks';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill, type StatusTone } from './StatusPill';
import { buttonClass } from './ui/Button';

// Webhook delivery telemetry as a data table (spec §3.15): timestamp, event
// id, type, status pill, latency. The timestamp cell is the expand control —
// each row opens to its JSON request body in a code block. Presenter — the
// webhooks route passes the deliveries.

export type DeliveryLogProps = {
  readonly deliveries: readonly Delivery[];
};

// A null status code is an in-flight delivery (#807) — neutral "pending" pill.
function statusTone(code: number | null): StatusTone {
  if (code === null) return 'neutral';
  if (code >= 500) return 'danger';
  if (code >= 400) return 'warning';
  return 'success';
}

export function DeliveryLog(props: DeliveryLogProps): JSX.Element {
  const [openId, setOpenId] = createSignal<string | null>(null);
  const toggle = (id: string): void => {
    setOpenId((cur) => (cur === id ? null : id));
  };

  const columns: readonly DataTableColumn<Delivery>[] = [
    {
      key: 'time',
      header: t('account_webhooks.col_time'),
      mono: true,
      cell: (d) => (
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'sm' })} delivery-log__toggle`}
          aria-expanded={openId() === d.id ? 'true' : 'false'}
          onClick={() => toggle(d.id)}
        >
          <IconChevronDown size="0.875rem" />
          {d.at}
        </button>
      ),
    },
    {
      key: 'event',
      rowHeader: true,
      header: t('account_webhooks.col_event'),
      mono: true,
      cell: (d) => d.event_id,
    },
    { key: 'type', header: t('account_webhooks.col_type'), mono: true, cell: (d) => d.type },
    {
      key: 'status',
      header: t('account_webhooks.col_status'),
      cell: (d) => (
        <StatusPill tone={statusTone(d.status_code)}>
          {d.status_code ?? t('account_webhooks.status_pending')}
        </StatusPill>
      ),
    },
    {
      key: 'latency',
      header: t('account_webhooks.col_latency'),
      cell: (d) =>
        d.latency_ms != null ? t('account_webhooks.latency_ms', { ms: d.latency_ms }) : UNSET,
    },
  ];

  return (
    <DataTable
      label={t('account_webhooks.deliveries_table_label')}
      columns={columns}
      rows={props.deliveries}
      rowKey={(d) => d.id}
      rowDetail={{
        open: (d) => openId() === d.id,
        render: (d) => (
          <>
            <p class="delivery-log__payload-label">{t('account_webhooks.payload_label')}</p>
            <pre class="code-block">{d.payload}</pre>
          </>
        ),
      }}
    />
  );
}
